import { shallowEqual, useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { getFrameBounds } from 'redux/db/util';
import * as S from './styles';
import { useMemo } from 'react';
import { Rect } from 'dragonmouse-shared/geometry';
import { getConnectingLine } from 'dragonmouse-shared/geometry';
import { selectTransform } from 'redux/mode/reducers';
import {
	DEFAULT_CANVAS_COLOR,
	selectChats,
	selectFrames,
	selectActiveScene,
} from 'redux/db/reducers';
import { contrastingForegroundColor } from 'utils';

interface KeyedLine {
	key: string;
	x1: number;
	y1: number;
	x2: number;
	y2: number;
}

const Connections = () => {
	const { frames, chats, scale, scene } = useSelector(
		(r: RootReducer) => ({
			frames: selectFrames(r.db),
			chats: selectChats(r.db),
			scale: selectTransform(r.mode).scale,
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	// Collect all connections
	const connections = useMemo(() => {
		const connections: KeyedLine[] = [];

		// each pending connection consists of
		type PendingConnection = {
			sourceFrameId: string;
			destinationFrameBounds: Rect;
			key: string;
		};
		const pendingConnections: PendingConnection[] = [];

		for (let frame of frames) {
			for (let sourceFrameId of frame.metadata.sourceFrameIds ?? []) {
				pendingConnections.push({
					key: `${frame.id}-${sourceFrameId}`,
					sourceFrameId,
					destinationFrameBounds: getFrameBounds(frame),
				});
			}
		}

		// originally i had these guts in each of the above for loops.  i moved it here to de-DRY
		// the code but, things are so entangled i'm not sure this makes it simpler - ss
		for (let pendingConnection of pendingConnections) {
			const sourceFrame = frames.find((f) => f.id === pendingConnection.sourceFrameId);
			if (!sourceFrame) continue;
			const line = getConnectingLine(
				pendingConnection.destinationFrameBounds,
				getFrameBounds(sourceFrame)
			);
			connections.push({
				key: pendingConnection.key,
				x1: line.start.x,
				y1: line.start.y,
				x2: line.end.x,
				y2: line.end.y,
			});
		}
		return connections;
	}, [frames, chats]);

	if (!connections.length) return null;

	// Calculate the bounds of all connections to set the viewBox
	const bounds = connections.reduce(
		(acc, conn) => ({
			minX: Math.min(acc.minX, conn.x1, conn.x2),
			minY: Math.min(acc.minY, conn.y1, conn.y2),
			maxX: Math.max(acc.maxX, conn.x1, conn.x2),
			maxY: Math.max(acc.maxY, conn.y1, conn.y2),
		}),
		{
			minX: connections[0].x1,
			minY: connections[0].y1,
			maxX: connections[0].x1,
			maxY: connections[0].y1,
		}
	);

	const boundsRect: Rect = {
		x: bounds.minX,
		y: bounds.minY,
		width: bounds.maxX - bounds.minX,
		height: bounds.maxY - bounds.minY,
	};

	// Add some padding to the bounds
	const viewBox = `${bounds.minX} ${bounds.minY} ${bounds.maxX - bounds.minX} ${
		bounds.maxY - bounds.minY
	}`;

	const foregroundColor = contrastingForegroundColor(
		scene?.metadata?.canvasColor ?? DEFAULT_CANVAS_COLOR
	);

	return (
		<>
			<S.SvgContainer sceneBounds={boundsRect} viewBox={viewBox} preserveAspectRatio="none">
				{connections.map((connection) => (
					<line
						key={connection.key}
						x1={connection.x1}
						y1={connection.y1}
						x2={connection.x2}
						y2={connection.y2}
						stroke={foregroundColor}
						strokeWidth={1 / scale}
					/>
				))}
			</S.SvgContainer>
		</>
	);
};

export default Connections;
